/**
 * Pure geo/projection helpers for MapRenderer — extracted for testability.
 */
import type { MapType } from '@course-authoring/shared-types';
import { geoCentroid, geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import usStatesTopology from './atlases/us-states-10m.json';

export const US_ISO_TO_FIPS: Record<string, string> = {
    'US-AL': '01', 'US-AK': '02', 'US-AZ': '04', 'US-AR': '05', 'US-CA': '06',
    'US-CO': '08', 'US-CT': '09', 'US-DE': '10', 'US-FL': '12', 'US-GA': '13',
    'US-HI': '15', 'US-ID': '16', 'US-IL': '17', 'US-IN': '18', 'US-IA': '19',
    'US-KS': '20', 'US-KY': '21', 'US-LA': '22', 'US-ME': '23', 'US-MD': '24',
    'US-MA': '25', 'US-MI': '26', 'US-MN': '27', 'US-MS': '28', 'US-MO': '29',
    'US-MT': '30', 'US-NE': '31', 'US-NV': '32', 'US-NH': '33', 'US-NJ': '34',
    'US-NM': '35', 'US-NY': '36', 'US-NC': '37', 'US-ND': '38', 'US-OH': '39',
    'US-OK': '40', 'US-OR': '41', 'US-PA': '42', 'US-RI': '44', 'US-SC': '45',
    'US-SD': '46', 'US-TN': '47', 'US-TX': '48', 'US-UT': '49', 'US-VT': '50',
    'US-VA': '51', 'US-WA': '53', 'US-WV': '54', 'US-WI': '55', 'US-WY': '56',
    'US-DC': '11', 'US-PR': '72', 'US-GU': '66', 'US-VI': '78', 'US-AS': '60', 'US-MP': '69',
};

export const US_FIPS_TO_ISO: Record<string, string> = {};
for (const [iso, fips] of Object.entries(US_ISO_TO_FIPS)) {
    US_FIPS_TO_ISO[fips] = iso;
}

export const US_FIPS_TO_ABBR_SHORT: Record<string, string> = {
    '60': 'AS', '66': 'GU', '69': 'MP', '72': 'PR', '78': 'VI',
};

export const CA_NAME_TO_KEY: Record<string, string> = {
    Alberta: 'CA-AB', 'British Columbia': 'CA-BC', Manitoba: 'CA-MB',
    'New Brunswick': 'CA-NB', 'Newfoundland and Labrador': 'CA-NL',
    'Nova Scotia': 'CA-NS', Ontario: 'CA-ON', 'Prince Edward Island': 'CA-PE',
    Quebec: 'CA-QC', Saskatchewan: 'CA-SK', 'Northwest Territories': 'CA-NT',
    Nunavut: 'CA-NU', Yukon: 'CA-YT', 'Yukon Territory': 'CA-YT',
};

export const ISO_ALPHA3_TO_NUMERIC: Record<string, string> = {
    GBR: '826', FRA: '250', DEU: '276', ITA: '380', ESP: '724',
    NLD: '528', BEL: '056', CHE: '756', AUT: '040', SWE: '752',
    NOR: '578', DNK: '208', FIN: '246', IRL: '372', PRT: '620',
    POL: '616', CZE: '203', ROU: '642', HUN: '348', GRC: '300',
    ISR: '376', ARE: '784', SAU: '682', ZAF: '710', NGA: '566',
    KEN: '404', EGY: '818', TUR: '792',
    AUS: '036', JPN: '392', CHN: '156', IND: '356', KOR: '410',
    SGP: '702', NZL: '554', IDN: '360', THA: '764', MYS: '458',
    PHL: '608', VNM: '704', TWN: '158', HKG: '344', PAK: '586', BGD: '050',
    MEX: '484', BRA: '076', ARG: '032', COL: '170', CHL: '152',
    PER: '604', VEN: '862', ECU: '218', BOL: '068', PRY: '600',
    URY: '858', GTM: '320', CRI: '188', PAN: '591', DOM: '214',
    CUB: '192', HND: '340', SLV: '222', NIC: '558', CAN: '124',
};

export const ISO_NUMERIC_TO_ALPHA3: Record<string, string> = {};
for (const [alpha, num] of Object.entries(ISO_ALPHA3_TO_NUMERIC)) {
    ISO_NUMERIC_TO_ALPHA3[num] = alpha;
}

export interface GeoLike {
    id?: string | number;
    properties?: { name?: string };
}

export function matchGeoToRegionKey(
    geo: GeoLike,
    mapType: MapType,
    regionKeys: Set<string>
): string | null {
    const geoId = String(geo.id ?? '');

    if (mapType === 'us') {
        const isoKey = US_FIPS_TO_ISO[geoId];
        if (isoKey && regionKeys.has(isoKey)) return isoKey;
        return null;
    }

    if (mapType === 'canada') {
        const name = geo.properties?.name;
        if (name) {
            const key = CA_NAME_TO_KEY[name];
            if (key && regionKeys.has(key)) return key;
        }
        return null;
    }

    // World maps
    const alpha3 = ISO_NUMERIC_TO_ALPHA3[geoId];
    if (alpha3 && regionKeys.has(alpha3)) return alpha3;
    return null;
}

export const TERRITORY_LAYOUT: Record<string, { x: number; y: number; scale: number }> = {
    GU: { x: 125, y: 580, scale: 8000 },
    MP: { x: 200, y: 580, scale: 1500 },
    AS: { x: 435, y: 600, scale: 6000 },
    PR: { x: 540, y: 598, scale: 2500 },
    VI: { x: 650, y: 590, scale: 4000 },
};

export function computeTerritoryPaths(): Record<string, string | string[]> {
    const result: Record<string, string | string[]> = {};
    try {
        const topoData = usStatesTopology as any;
        const objectKey = Object.keys(topoData.objects)[0];
        if (!objectKey) return result;
        const features = (feature(topoData, topoData.objects[objectKey]) as any).features;
        features.forEach((feat: any) => {
            const abbr = feat.id != null ? US_FIPS_TO_ABBR_SHORT[String(feat.id)] : undefined;
            if (!abbr || !TERRITORY_LAYOUT[abbr]) return;
            const centroid = geoCentroid(feat);
            const layout = TERRITORY_LAYOUT[abbr];
            const projection = geoMercator().center(centroid).scale(layout.scale).translate([0, 0]);
            const pathGenerator = geoPath(projection);
            const geom = feat.geometry;
            if (geom?.type === 'MultiPolygon' && geom.coordinates?.length > 1) {
                result[abbr] = geom.coordinates.map(
                    (polygon: any) => pathGenerator({ type: 'Polygon', coordinates: polygon }) || ''
                );
            } else {
                result[abbr] = pathGenerator(feat) || '';
            }
        });
    } catch { /* topology parse failure — territories won't render shapes */ }
    return result;
}
